import React, { useState, useEffect } from 'react';
import { clientService } from '../../../services/clientService';
import { useTheme } from '../../../contexts/ThemeContext';

const EditClientModal = ({ isOpen, client, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    id: '',
    nom: '',
    prenom: '',
    tel: '',
    login: '',
    password: '',
    discriminator: '',
    isClient: true,
    raisonSocial: '',
    matriculeFiscal: '',
    timberFiscal: false,
    fodec: false,
    assujeti: false,
    exonore: false,
    observation: '',
    annuelle: false,
    logo: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Initialize form data when client changes
  useEffect(() => {
    if (client) {
      setFormData({
        id: client.id || '',
        nom: client.nom || '',
        prenom: client.prenom || '',
        tel: client.tel || '',
        login: client.login || '',
        password: client.password, // never prefill password
        raisonSocial: client.raisonSocial || '',
        matriculeFiscal: client.matriculeFiscal || '',
        timberFiscal: client.timberFiscal || false,
        fodec: client.fodec || false,
        assujeti: client.assujeti || false,
        exonore: client.exonore || false,
        observation: client.observation || '',
        annuelle: client.annuelle || false,
        logo: client.logo || null
      });
      if (client.logo) {
        setLogoPreview(`data:image/jpeg;base64,${client.logo}`);
      } else {
        setLogoPreview(null);
      }
    }
  }, [client]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setError('Le fichier logo ne peut pas dépasser 5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result.split(',')[1];
        setFormData(prev => ({ ...prev, logo: base64 }));
        setLogoPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.raisonSocial.trim()) {
        throw new Error('La raison sociale est obligatoire');
      }
      if (!formData.login.trim()) {
        throw new Error('Le login est obligatoire');
      }
      if (!formData.nom.trim()) {
        throw new Error('Le nom est obligatoire');
      }
      if (!formData.prenom.trim()) {
        throw new Error('Le prénom est obligatoire');
      }

      // If password is empty in edit mode, do not send it
      let payload = { ...formData };
      if (!!client?.id && !formData.password) {
        const { password, ...rest } = payload;
        payload = rest;
      }

      // id should be a number if present
      if (payload.id === '') delete payload.id;

      const result = await clientService.updateClient(client.id, payload);
      
      if (result.success) {
        onSuccess();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-8 py-6 ${
          isDarkMode 
            ? 'bg-gray-800' 
            : 'bg-gradient-to-r from-primary-600 to-primary-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl backdrop-blur-sm flex items-center justify-center mr-6 shadow-lg">
                <i className="fas fa-edit text-white text-2xl"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-1">Modifier le Client</h2>
                <div className="flex items-center space-x-4">
                  <p className="text-primary-100 flex items-center">
                    <i className="fas fa-building mr-2"></i>
                    {client.raisonSocial}
                  </p>
                  <div className="px-3 py-1 bg-white bg-opacity-20 rounded-full">
                    <span className="text-primary-100 text-sm">ID: {client.id}</span>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-xl p-3 transition-all duration-200 hover:scale-105"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-140px)]">
          <form onSubmit={handleSubmit} className="p-8">
            {/* Error Message */}
            {error && (
              <div className={`mb-8 border-l-4 border-l-red-500 p-6 rounded-r-2xl shadow-sm ${
                isDarkMode 
                  ? 'bg-red-900/50 text-red-300' 
                  : 'bg-red-50 text-red-700'
              }`}>
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-4 ${
                    isDarkMode ? 'bg-red-800' : 'bg-red-100'
                  }`}>
                    <i className={`fas fa-exclamation-circle ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}></i>
                  </div>
                  <div>
                    <h4 className={`font-semibold mb-1 ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>Erreur de validation</h4>
                    <span className={isDarkMode ? 'text-red-400' : 'text-red-700'}>{error}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-8">
                {/* Basic Information */}
                <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                  <h3 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center mr-3">
                      <i className="fas fa-info-circle text-primary-600"></i>
                    </div>
                    Informations de base
                  </h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>ID</label>
                        <input
                          type="text"
                          name="id"
                          value={formData.id}
                          readOnly
                          className={`w-full px-4 py-4 border rounded-xl shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-gray-400' 
                              : 'border-gray-200 bg-gray-100 text-gray-500'
                          }`}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Nom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="nom"
                          value={formData.nom}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="Nom du client"
                          required
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Prénom <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="prenom"
                          value={formData.prenom}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="Prénom du client"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Téléphone</label>
                        <input
                          type="tel"
                          name="tel"
                          value={formData.tel}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="+216 XX XXX XXX"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Raison Sociale <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="raisonSocial"
                          value={formData.raisonSocial}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="Nom de l'entreprise"
                          required
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Login <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="login"
                          value={formData.login}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="Login utilisateur"
                          required
                        />
                      </div>
                      <div className="relative">
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Mot de passe {!!client?.id ? <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>(laisser vide pour ne pas changer)</span> : <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm pr-12 ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="Mot de passe"
                          autoComplete="new-password"
                          {...(!!client?.id ? {} : { required: true })}
                        />
                        <button
                          type="button"
                          tabIndex={-1}
                          className={`absolute right-3 bottom-4 hover:text-primary-600 focus:outline-none ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-400'
                          }`}
                          style={{padding: 0, height: '2rem', width: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.22 1.125-4.575m1.875-2.25A9.956 9.956 0 0112 3c5.523 0 10 4.477 10 10 0 1.657-.403 3.22-1.125-4.575m-1.875 2.25A9.956 9.956 0 0112 21c-5.523 0-10-4.477-10-10 0-1.657.403-3.22 1.125-4.575" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.22 1.125-4.575m1.875-2.25A9.956 9.956 0 0112 3c5.523 0 10 4.477 10 10 0 1.657-.403 3.22-1.125-4.575m-1.875 2.25A9.956 9.956 0 0112 21c-5.523 0-10-4.477-10-10 0-1.657.403-3.22 1.125-4.575" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className={`block text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Matricule Fiscal</label>
                        <input
                          type="text"
                          name="matriculeFiscal"
                          value={formData.matriculeFiscal}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-4 border rounded-xl focus:ring-2 focus:border-transparent transition-all duration-200 text-lg shadow-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                              : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                          }`}
                          placeholder="XXXXXXX/X/X/X/XXX"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200'}`}>
                  <h3 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mr-3">
                      <i className="fas fa-image text-indigo-600"></i>
                    </div>
                    Logo de l'entreprise
                  </h3>
                  
                  <div className="flex flex-col lg:flex-row items-start space-y-6 lg:space-y-0 lg:space-x-6">
                    {logoPreview && (
                      <div className="flex-shrink-0 relative">
                        <img
                          src={logoPreview}
                          alt="Preview"
                          className="w-24 h-24 object-contain rounded-2xl border-2 border-gray-200 bg-white shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600 transition-all duration-200 shadow-lg hover:scale-110"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload-edit"
                      />
                      <label
                        htmlFor="logo-upload-edit"
                        className={`block w-full p-6 border-2 border-dashed rounded-2xl text-center cursor-pointer transition-all duration-200 shadow-sm ${
                          isDarkMode
                            ? 'border-indigo-600 hover:border-indigo-500 hover:bg-indigo-900/20'
                            : 'border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50'
                        }`}
                      >
                        <i className="fas fa-cloud-upload-alt text-3xl text-indigo-400 mb-3"></i>
                        <p className={`text-base font-medium mb-1 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                          {logoPreview ? 'Changer le logo' : 'Ajouter un logo'}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-indigo-400' : 'text-indigo-500'}`}>
                          PNG, JPG jusqu'à 5MB
                        </p>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Status Toggles */}
                <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200'}`}>
                  <h3 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center mr-3">
                      <i className="fas fa-cogs text-emerald-600"></i>
                    </div>
                    Paramètres fiscaux
                  </h3>
                  <div className="space-y-4">
                    <div className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-receipt text-emerald-600"></i>
                        </div>
                        <div>
                          <label className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Timber Fiscal</label>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Soumis au timber fiscal</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="timberFiscal"
                          checked={formData.timberFiscal}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    <div className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-percentage text-blue-600"></i>
                        </div>
                        <div>
                          <label className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Assujetti</label>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Assujetti à la TVA</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="assujeti"
                          checked={formData.assujeti}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    <div className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-ban text-orange-600"></i>
                        </div>
                        <div>
                          <label className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Exonéré</label>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Exonéré de certaines taxes</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="exonore"
                          checked={formData.exonore}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                    <div className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-calendar-alt text-purple-600"></i>
                        </div>
                        <div className="flex-1">
                          <label className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Entretien</label>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Choisissez la périodicité</p>
                          <div className={`mt-3 flex rounded-full p-1 w-fit ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, annuelle: true }))}
                              className={`px-4 py-1 rounded-full font-medium transition-all duration-200 ${
                                formData.annuelle
                                  ? 'bg-primary-600 text-white shadow'
                                  : isDarkMode
                                  ? 'text-gray-300 hover:bg-gray-600'
                                  : 'text-gray-700 hover:bg-gray-300'
                              }`}
                              style={{ minWidth: 110 }}
                            >
                              Annuelle
                            </button>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, annuelle: false }))}
                              className={`px-4 py-1 rounded-full font-medium transition-all duration-200 ${
                                !formData.annuelle
                                  ? 'bg-primary-600 text-white shadow'
                                  : isDarkMode
                                  ? 'text-gray-300 hover:bg-gray-600'
                                  : 'text-gray-700 hover:bg-gray-300'
                              }`}
                              style={{ minWidth: 110 }}
                            >
                              Semestrielle
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={`flex items-center justify-between p-4 rounded-2xl border shadow-sm ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mr-4">
                          <i className="fas fa-certificate text-pink-600"></i>
                        </div>
                        <div>
                          <label className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>FODEC</label>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Soumis au FODEC</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          name="fodec"
                          checked={formData.fodec}
                          onChange={handleInputChange}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-primary-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Observations */}
                <div className={`rounded-2xl p-6 border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200'}`}>
                  <h3 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mr-3">
                      <i className="fas fa-comment-alt text-amber-600"></i>
                    </div>
                    Observations
                  </h3>
                  
                  <textarea
                    name="observation"
                    value={formData.observation}
                    onChange={handleInputChange}
                    rows={6}
                    className={`w-full px-4 py-4 border rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 resize-none shadow-sm text-base ${
                      isDarkMode 
                        ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-400 focus:ring-dark-primary-500' 
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-primary-500'
                    }`}
                    placeholder="Notes et observations sur ce client..."
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4 mt-12 pt-8 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                type="button"
                onClick={handleClose}
                className={`px-8 py-4 rounded-xl transition-all duration-200 font-semibold shadow-sm hover:shadow-md ${
                  isDarkMode 
                    ? 'text-gray-300 bg-gray-700 hover:bg-gray-600' 
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <i className="fas fa-times mr-2"></i>
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-4 bg-primary-600 text-white hover:bg-primary-700 rounded-xl transition-all duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Sauvegarde en cours...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Sauvegarder les modifications
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditClientModal;